const RINGING_AUDIO_PREF_KEY = "ringing_audio_enabled";
const CALL_AUDIO_DEBUG_PREFIX = "[CallAudioDebug]";

const logAudioDebug = (
  step: string,
  status: "start" | "ok" | "fail" | "info",
  details?: Record<string, unknown>,
) => {
  if (details) {
    console.info(`${CALL_AUDIO_DEBUG_PREFIX} ${step} ${status}`, details);
    return;
  }
  console.info(`${CALL_AUDIO_DEBUG_PREFIX} ${step} ${status}`);
};

class CallAudioFeedbackService {
  private audioContext: AudioContext | null = null;
  private ringbackIntervalId: ReturnType<typeof setInterval> | null = null;
  private ringbackTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private activeOscillator: OscillatorNode | null = null;
  private activeGain: GainNode | null = null;

  private canUseWebAudio(): boolean {
    return (
      typeof window !== "undefined" &&
      typeof window.AudioContext !== "undefined"
    );
  }

  private getAudioContext(): AudioContext {
    if (!this.audioContext) {
      this.audioContext = new window.AudioContext();
    }
    return this.audioContext;
  }

  isAudioEnabled(): boolean {
    if (typeof window === "undefined") {
      return true;
    }

    try {
      const storedPreference = window.localStorage.getItem(
        RINGING_AUDIO_PREF_KEY,
      );
      return storedPreference !== "false";
    } catch {
      return true;
    }
  }

  setAudioEnabled(enabled: boolean): void {
    if (typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(RINGING_AUDIO_PREF_KEY, String(enabled));
    } catch {
      // Ignore storage errors to avoid breaking call flow.
    }
  }

  async primeAudio(): Promise<void> {
    logAudioDebug("prime_audio", "start");
    if (!this.canUseWebAudio()) {
      logAudioDebug("prime_audio", "fail", { reason: "web_audio_unavailable" });
      return;
    }

    const context = this.getAudioContext();
    if (context.state === "suspended") {
      await context.resume();
    }
    logAudioDebug("prime_audio", "ok", { contextState: context.state });
  }

  private startRingbackPulse = (): void => {
    if (!this.audioContext) {
      return;
    }

    const oscillator = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();

    oscillator.type = "sine";
    oscillator.frequency.value = 440;
    gain.gain.value = 0.03;

    oscillator.connect(gain);
    gain.connect(this.audioContext.destination);

    oscillator.start();
    this.activeOscillator = oscillator;
    this.activeGain = gain;

    this.ringbackTimeoutId = setTimeout(() => {
      this.stopActiveTone();
    }, 1800);
  };

  private stopActiveTone(): void {
    if (this.ringbackTimeoutId) {
      clearTimeout(this.ringbackTimeoutId);
      this.ringbackTimeoutId = null;
    }

    if (this.activeOscillator) {
      try {
        this.activeOscillator.stop();
      } catch {
        // Already stopped.
      }
      this.activeOscillator.disconnect();
      this.activeOscillator = null;
    }

    if (this.activeGain) {
      this.activeGain.disconnect();
      this.activeGain = null;
    }
  }

  async playRingback(): Promise<void> {
    logAudioDebug("play_ringback", "start");
    if (!this.canUseWebAudio()) {
      logAudioDebug("play_ringback", "fail", {
        reason: "web_audio_unavailable",
      });
      return;
    }

    if (!this.isAudioEnabled()) {
      logAudioDebug("play_ringback", "fail", {
        reason: "ringing_audio_disabled",
      });
      return;
    }

    if (this.ringbackIntervalId) {
      logAudioDebug("play_ringback", "info", { reason: "already_playing" });
      return;
    }

    const context = this.getAudioContext();
    if (context.state === "suspended") {
      await context.resume();
    }

    this.startRingbackPulse();
    this.ringbackIntervalId = setInterval(this.startRingbackPulse, 4000);
    logAudioDebug("play_ringback", "ok", { contextState: context.state });
  }

  stopRingback(): void {
    if (this.ringbackIntervalId) {
      clearInterval(this.ringbackIntervalId);
      this.ringbackIntervalId = null;
    }

    this.stopActiveTone();
    logAudioDebug("stop_ringback", "ok");
  }

  stopAll(): void {
    this.stopRingback();
  }
}

export const callAudioFeedback = new CallAudioFeedbackService();
